import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import AdminAPI from '../api/adminApi';

const InterviewMediaUpload = () => {
  const navigate = useNavigate();
  const { user, applicantStatus, refreshApplicantStatus } = useAuth();
  const [formData, setFormData] = React.useState({
    driversLicense: [],
    workspaceImages: [],
    washingMachineLabel: [],
    foldedItemsImages: [],
    introVideo: null,
  });
  const [previews, setPreviews] = React.useState({
    driversLicense: [],
    workspaceImages: [],
    washingMachineLabel: [],
    foldedItemsImages: [],
    introVideo: null,
  });
  const [errors, setErrors] = React.useState({});
  const [uploading, setUploading] = React.useState(false);
  const [uploadProgress, setUploadProgress] = React.useState(0);
  const [uploadSuccess, setUploadSuccess] = React.useState(false);
  const [compressing, setCompressing] = React.useState(false);
  const [compressionProgress, setCompressionProgress] = React.useState(0);

  React.useEffect(() => {
    window.scrollTo(0, 0);
    // Check if user status is 'interview'
    if (applicantStatus !== 'interview') {
      navigate('/');
    }
  }, [user, navigate, applicantStatus]);

  const validateFile = (file, type) => {
    const errors = [];

    if (type === 'image') {
      const allowedTypes = ['image/jpeg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        errors.push('Only JPG and PNG files are allowed');
      }
      if (file.size > 10 * 1024 * 1024) {
        errors.push('Image size must be less than 10MB');
      }
    } else if (type === 'video') {
      const allowedTypes = [
        'video/mp4',
        'video/quicktime',
        'video/x-msvideo',
        'video/webm',
      ];
      if (!allowedTypes.includes(file.type)) {
        errors.push('Only MP4, MOV, AVI, and WEBM files are allowed');
      }

      // Check video duration (6 minutes = 360 seconds)
      return new Promise((resolve) => {
        const video = document.createElement('video');
        video.preload = 'metadata';
        video.onloadedmetadata = () => {
          window.URL.revokeObjectURL(video.src);
          if (video.duration > 360) {
            errors.push('Video must be 6 minutes or less');
          }
          resolve(errors);
        };
        video.onerror = () => {
          errors.push('Unable to validate video file');
          resolve(errors);
        };
        video.src = URL.createObjectURL(file);
      });
    }

    return errors;
  };

  const compressImage = async (file) => {
    // Compress images larger than 1MB to be under 1MB
    const MAX_SIZE = 1 * 1024 * 1024; // 1MB

    if (file.size <= MAX_SIZE) {
      console.log('Image size acceptable, no compression needed');
      return file;
    }

    console.log(
      `Compressing image: ${(file.size / (1024 * 1024)).toFixed(2)}MB`
    );

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          // Calculate dimensions to reduce file size while maintaining aspect ratio
          const maxDimension = 1920; // Max width or height
          if (width > maxDimension || height > maxDimension) {
            if (width > height) {
              height = Math.round((height / width) * maxDimension);
              width = maxDimension;
            } else {
              width = Math.round((width / height) * maxDimension);
              height = maxDimension;
            }
          }

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          // Start with quality 0.8 and reduce if needed
          let quality = 0.8;
          const tryCompress = () => {
            canvas.toBlob(
              (blob) => {
                if (blob.size > MAX_SIZE && quality > 0.3) {
                  // If still too large, reduce quality and try again
                  quality -= 0.1;
                  tryCompress();
                } else {
                  const compressedFile = new File([blob], file.name, {
                    type: 'image/jpeg',
                    lastModified: Date.now(),
                  });

                  const originalSize = (file.size / (1024 * 1024)).toFixed(2);
                  const compressedSize = (
                    compressedFile.size /
                    (1024 * 1024)
                  ).toFixed(2);
                  const reduction = (
                    (1 - compressedFile.size / file.size) *
                    100
                  ).toFixed(1);

                  console.log(`✅ Image compression complete!`);
                  console.log(
                    `Original: ${originalSize}MB → Compressed: ${compressedSize}MB`
                  );
                  console.log(`Size reduced by ${reduction}%`);

                  resolve(compressedFile);
                }
              },
              'image/jpeg',
              quality
            );
          };

          tryCompress();
        };
        img.onerror = reject;
      };
      reader.onerror = reject;
    });
  };

  const compressVideo = async (file) => {
    // Simple client-side video compression using Canvas API
    const MAX_SIZE = 100 * 1024 * 1024; // 100MB threshold for compression

    if (file.size < MAX_SIZE) {
      console.log('Video size acceptable, no compression needed');
      return file;
    }

    setCompressing(true);
    setCompressionProgress(0);
    console.log('Starting video compression...');

    try {
      // Create video element to read the file
      const video = document.createElement('video');
      video.preload = 'metadata';

      const videoURL = URL.createObjectURL(file);
      video.src = videoURL;

      await new Promise((resolve, reject) => {
        video.onloadedmetadata = resolve;
        video.onerror = reject;
      });

      const duration = video.duration;
      const width = video.videoWidth;
      const height = video.videoHeight;

      // Calculate target dimensions (max 720p)
      let targetWidth = width;
      let targetHeight = height;
      const maxDimension = 1280;

      if (width > maxDimension || height > maxDimension) {
        if (width > height) {
          targetWidth = maxDimension;
          targetHeight = Math.round((height / width) * maxDimension);
        } else {
          targetHeight = maxDimension;
          targetWidth = Math.round((width / height) * maxDimension);
        }
      }

      console.log(
        `Original: ${width}x${height}, Target: ${targetWidth}x${targetHeight}`
      );

      // Calculate target bitrate for 65% reduction
      // Original file size / duration gives approximate bitrate
      // We want 35% of original size (65% reduction)
      const originalBitrate = (file.size * 8) / duration; // bits per second
      const targetBitrate = Math.round(originalBitrate * 0.35); // 35% of original = 65% reduction
      const minBitrate = 2000000; // 2 Mbps minimum for quality
      const maxBitrate = 8000000; // 8 Mbps maximum
      const finalBitrate = Math.max(
        minBitrate,
        Math.min(targetBitrate, maxBitrate)
      );

      console.log(
        `Original bitrate: ${(originalBitrate / 1000000).toFixed(2)} Mbps`
      );
      console.log(
        `Target bitrate: ${(finalBitrate / 1000000).toFixed(
          2
        )} Mbps for 65% reduction`
      );

      // Create canvas for processing
      const canvas = document.createElement('canvas');
      canvas.width = targetWidth;
      canvas.height = targetHeight;
      const ctx = canvas.getContext('2d');

      // Get canvas video stream
      const canvasStream = canvas.captureStream(30); // 30 fps

      // Create a new MediaStream combining video from canvas and audio from original
      const combinedStream = new MediaStream();

      // Add video track from canvas
      const videoTrack = canvasStream.getVideoTracks()[0];
      combinedStream.addTrack(videoTrack);

      // Extract and add audio track from original video
      try {
        // Create a temporary video element to extract audio
        const audioContext = new AudioContext();
        const source = audioContext.createMediaElementSource(video);
        const destination = audioContext.createMediaStreamDestination();
        source.connect(destination);

        // Add audio track if available
        if (destination.stream.getAudioTracks().length > 0) {
          const audioTrack = destination.stream.getAudioTracks()[0];
          combinedStream.addTrack(audioTrack);
          console.log('✅ Audio track added to compressed video');
        } else {
          console.log('⚠️ No audio track found in original video');
        }
      } catch (audioError) {
        console.warn('Could not extract audio:', audioError);
      }

      // Use MediaRecorder API for compression with combined stream
      const mediaRecorder = new MediaRecorder(combinedStream, {
        mimeType: 'video/webm;codecs=vp9,opus',
        videoBitsPerSecond: finalBitrate,
        audioBitsPerSecond: 128000, // 128 kbps for audio
      });

      const chunks = [];
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunks.push(e.data);
        }
      };

      return new Promise((resolve, reject) => {
        mediaRecorder.onstop = async () => {
          const compressedBlob = new Blob(chunks, { type: 'video/webm' });
          const compressedFile = new File(
            [compressedBlob],
            file.name.replace(/\.[^/.]+$/, '.webm'),
            {
              type: 'video/webm',
              lastModified: Date.now(),
            }
          );

          const originalSize = (file.size / (1024 * 1024)).toFixed(2);
          const compressedSize = (compressedFile.size / (1024 * 1024)).toFixed(
            2
          );
          const reduction = (
            (1 - compressedFile.size / file.size) *
            100
          ).toFixed(1);

          console.log(`✅ Compression complete!`);
          console.log(
            `Original: ${originalSize}MB → Compressed: ${compressedSize}MB`
          );
          console.log(`Size reduced by ${reduction}%`);

          URL.revokeObjectURL(videoURL);
          setCompressing(false);
          setCompressionProgress(0);
          resolve(compressedFile);
        };

        mediaRecorder.onerror = (error) => {
          console.error('MediaRecorder error:', error);
          URL.revokeObjectURL(videoURL);
          setCompressing(false);
          setCompressionProgress(0);
          reject(error);
        };

        // Start recording and set up video playback
        mediaRecorder.start();

        // Reset video to start
        video.currentTime = 0;

        // Once video is seeked to start, begin playback
        video.onseeked = () => {
          video.play();

          const startTime = Date.now();
          let frameCount = 0;
          const fps = 30;
          const frameDuration = 1000 / fps; // milliseconds per frame

          const captureFrame = () => {
            if (video.ended || video.paused || video.currentTime >= duration) {
              mediaRecorder.stop();
              video.pause();
              return;
            }

            // Draw current frame to canvas
            ctx.drawImage(video, 0, 0, targetWidth, targetHeight);

            // Update progress based on video playback
            const progress = Math.round((video.currentTime / duration) * 100);
            setCompressionProgress(progress);

            frameCount++;
            const nextFrameTime = startTime + frameCount * frameDuration;
            const currentTime = Date.now();
            const delay = Math.max(0, nextFrameTime - currentTime);

            setTimeout(captureFrame, delay);
          };

          // Start capturing frames
          captureFrame();

          // Handle video end
          video.onended = () => {
            mediaRecorder.stop();
          };
        };
      });
    } catch (error) {
      console.error('Video compression error:', error);
      setCompressing(false);
      setCompressionProgress(0);

      alert(
        'Video compression failed. The original video will be uploaded. Note: Large videos may take longer to upload.'
      );

      return file;
    }
  };

  // Helper function to convert File to Uint8Array
  const fetchFileData = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        resolve(new Uint8Array(reader.result));
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  };

  const handleFileChange = async (
    e,
    fieldName,
    fileType,
    allowMultiple = false
  ) => {
    const files = allowMultiple
      ? Array.from(e.target.files)
      : [e.target.files[0]];
    if (!files.length || !files[0]) return;

    let allValid = true;
    const validFiles = [];

    for (const file of files) {
      let validationErrors = [];
      if (fileType === 'video') {
        validationErrors = await validateFile(file, fileType);
      } else {
        validationErrors = validateFile(file, fileType);
      }

      if (validationErrors.length > 0) {
        setErrors({ ...errors, [fieldName]: validationErrors.join(', ') });
        allValid = false;
        break;
      }
      validFiles.push(file);
    }

    if (!allValid) return;

    // Clear errors for this field
    const newErrors = { ...errors };
    delete newErrors[fieldName];
    setErrors(newErrors);

    // Process files
    const processedFiles = [];
    for (const file of validFiles) {
      let processedFile = file;
      if (fileType === 'video') {
        processedFile = await compressVideo(file);
      } else if (fileType === 'image') {
        processedFile = await compressImage(file);
      }
      processedFiles.push(processedFile);
    }

    // Update form data
    if (allowMultiple) {
      const existingFiles = formData[fieldName] || [];
      setFormData({
        ...formData,
        [fieldName]: [...existingFiles, ...processedFiles],
      });
    } else {
      setFormData({ ...formData, [fieldName]: processedFiles[0] });
    }

    // Create previews
    const newPreviews = [];
    for (const file of processedFiles) {
      const reader = new FileReader();
      await new Promise((resolve) => {
        reader.onloadend = () => {
          newPreviews.push(reader.result);
          resolve();
        };
        reader.readAsDataURL(file);
      });
    }

    if (allowMultiple) {
      const existingPreviews = previews[fieldName] || [];
      setPreviews({
        ...previews,
        [fieldName]: [...existingPreviews, ...newPreviews],
      });
    } else {
      setPreviews({ ...previews, [fieldName]: newPreviews[0] });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.driversLicense || formData.driversLicense.length === 0) {
      newErrors.driversLicense =
        "At least one driver's license image is required";
    }
    if (!formData.workspaceImages || formData.workspaceImages.length === 0) {
      newErrors.workspaceImages = 'At least one workspace image is required';
    }
    if (
      !formData.washingMachineLabel ||
      formData.washingMachineLabel.length === 0
    ) {
      newErrors.washingMachineLabel =
        'At least one washing machine label image is required';
    }
    if (
      !formData.foldedItemsImages ||
      formData.foldedItemsImages.length === 0
    ) {
      newErrors.foldedItemsImages =
        'At least one folded items image is required';
    }
    if (!formData.introVideo) {
      newErrors.introVideo = 'Introduction video is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      alert('Please upload all required files');
      return;
    }

    if (!user || !user.id) {
      alert('User not authenticated. Please log in again.');
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      // Step 1: Upload all files to Strapi media library
      const uploadedMediaIds = {
        driversLicense: [],
        workspaceImages: [],
        washingMachineLabel: [],
        foldedItemsImages: [],
        introVideo: null,
      };

      let uploadCount = 0;
      const totalFiles =
        (formData.driversLicense?.length || 0) +
        (formData.workspaceImages?.length || 0) +
        (formData.washingMachineLabel?.length || 0) +
        (formData.foldedItemsImages?.length || 0) +
        1; // introVideo

      // Upload Driver's License (multiple)
      if (formData.driversLicense && formData.driversLicense.length > 0) {
        for (const file of formData.driversLicense) {
          const uploadFormData = new FormData();
          uploadFormData.append('files', file);
          const uploadedFiles = await AdminAPI.uploadMedia(uploadFormData);
          uploadedMediaIds.driversLicense.push(uploadedFiles[0].id);
          uploadCount++;
          setUploadProgress((uploadCount / totalFiles) * 100);
        }
      }

      // Upload Workspace Images (multiple)
      if (formData.workspaceImages && formData.workspaceImages.length > 0) {
        for (const file of formData.workspaceImages) {
          const uploadFormData = new FormData();
          uploadFormData.append('files', file);
          const uploadedFiles = await AdminAPI.uploadMedia(uploadFormData);
          uploadedMediaIds.workspaceImages.push(uploadedFiles[0].id);
          uploadCount++;
          setUploadProgress((uploadCount / totalFiles) * 100);
        }
      }

      // Upload Washing Machine Label (multiple)
      if (
        formData.washingMachineLabel &&
        formData.washingMachineLabel.length > 0
      ) {
        for (const file of formData.washingMachineLabel) {
          const uploadFormData = new FormData();
          uploadFormData.append('files', file);
          const uploadedFiles = await AdminAPI.uploadMedia(uploadFormData);
          uploadedMediaIds.washingMachineLabel.push(uploadedFiles[0].id);
          uploadCount++;
          setUploadProgress((uploadCount / totalFiles) * 100);
        }
      }

      // Upload Folded Items Images (multiple)
      if (formData.foldedItemsImages && formData.foldedItemsImages.length > 0) {
        for (const file of formData.foldedItemsImages) {
          const uploadFormData = new FormData();
          uploadFormData.append('files', file);
          const uploadedFiles = await AdminAPI.uploadMedia(uploadFormData);
          uploadedMediaIds.foldedItemsImages.push(uploadedFiles[0].id);
          uploadCount++;
          setUploadProgress((uploadCount / totalFiles) * 100);
        }
      }

      // Upload Introduction Video
      if (formData.introVideo) {
        const uploadFormData = new FormData();
        uploadFormData.append('files', formData.introVideo);
        const uploadedFiles = await AdminAPI.uploadMedia(uploadFormData);
        uploadedMediaIds.introVideo = uploadedFiles[0].id;
        uploadCount++;
        setUploadProgress((uploadCount / totalFiles) * 100);
      }

      // Step 2: Get the applicant record for this user
      console.log('Current user ID:', user.id);
      const applicantResponse = await AdminAPI.getApplicantByUserId(user.id);
      console.log('Applicant response:', applicantResponse);

      // Check if we have data
      if (
        !applicantResponse ||
        !applicantResponse.data ||
        applicantResponse.data.length === 0
      ) {
        console.error('No applicant records found in response');
        throw new Error(
          'No applicant record found for this user. Please ensure you have completed your application.'
        );
      }

      const applicantDocumentId = applicantResponse.data[0]?.documentId;
      console.log('Applicant documentId:', applicantDocumentId);
      console.log('Full applicant data:', applicantResponse.data[0]);

      if (!applicantDocumentId) {
        console.error('No applicant documentId found for user:', user.id);
        throw new Error('Applicant record not found. Please contact support.');
      }

      // Step 3: Update applicant record with mediaData component
      console.log('Updating applicant with documentId:', applicantDocumentId);
      console.log('Media IDs:', uploadedMediaIds);

      try {
        await AdminAPI.createMediaData(applicantDocumentId, uploadedMediaIds);
        console.log('✅ Media data updated successfully');
      } catch (mediaError) {
        console.error('Error updating media data:', mediaError);
        console.error('Error response:', mediaError.response?.data);
        throw new Error(
          `Failed to update media data: ${
            mediaError.response?.data?.error?.message || mediaError.message
          }`
        );
      }

      // Step 4: Update applicant applicationStatus to 'interviewReview'
      console.log('Updating applicant applicationStatus to interviewReview...');
      try {
        await AdminAPI.updateApplicantStatus(
          applicantDocumentId,
          'interviewReview'
        );
        console.log('✅ Applicant applicationStatus updated successfully');
      } catch (statusError) {
        console.error('Error updating applicant status:', statusError);
        // Don't throw error here, media is already uploaded
        console.warn(
          'Failed to update applicationStatus but media upload was successful'
        );
      }
      window.scrollTo(0, 0);
      // Refresh applicant status in context
      await refreshApplicantStatus();

      setUploadSuccess(true);
    } catch (error) {
      console.error('Upload error:', error);
      console.error('Error details:', error.response?.data || error.message);

      let errorMessage = 'Failed to upload media. Please try again.';

      if (error.message.includes('Applicant record not found')) {
        errorMessage =
          'No applicant record found. Please complete your application first.';
      } else if (error.response?.status === 404) {
        errorMessage = 'Applicant record not found. Please contact support.';
      } else if (error.response?.status === 403) {
        errorMessage = 'Permission denied. Please log in again.';
      }

      alert(errorMessage);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const removeFile = (fieldName, index = null) => {
    if (index !== null) {
      // Remove specific file from array
      const updatedFiles = [...(formData[fieldName] || [])];
      updatedFiles.splice(index, 1);
      setFormData({ ...formData, [fieldName]: updatedFiles });

      const updatedPreviews = [...(previews[fieldName] || [])];
      updatedPreviews.splice(index, 1);
      setPreviews({ ...previews, [fieldName]: updatedPreviews });
    } else {
      // Remove single file
      setFormData({ ...formData, [fieldName]: null });
      setPreviews({ ...previews, [fieldName]: null });
    }

    const newErrors = { ...errors };
    delete newErrors[fieldName];
    setErrors(newErrors);
  };

  const VideoDropZone = () => {
    const [isDragging, setIsDragging] = React.useState(false);
    const dragCounter = React.useRef(0);

    const handleDragEnter = (e) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounter.current++;
      if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
        setIsDragging(true);
      }
    };

    const handleDragLeave = (e) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounter.current--;
      if (dragCounter.current === 0) {
        setIsDragging(false);
      }
    };

    const handleDragOver = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const handleDrop = async (e) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounter.current = 0;
      setIsDragging(false);

      const files = e.dataTransfer.files;
      if (files && files.length > 0) {
        const syntheticEvent = {
          target: {
            files: [files[0]],
          },
        };
        await handleFileChange(syntheticEvent, 'introVideo', 'video', false);
      }
    };

    return (
      <div
        className={`flex bg-white border-2 border-dashed rounded-md w-full h-full py-4 px-3 mb-3 transition-colors ${
          isDragging ? 'border-blue-500 bg-blue-50' : 'border-portal-300'
        }`}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}>
        <label className="relative inline-flex items-center text-md text-portal-300 cursor-pointer w-full justify-center">
          <svg
            className="mr-2"
            width="22"
            height="22"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg">
            <path
              d="M4.93 19.071a5.86 5.86 0 0 1-.213-8.273l7.191-7.191a4.24 4.24 0 0 1 5.982.141 4.24 4.24 0 0 1 .149 5.99l-7.198 7.198a2.67 2.67 0 0 1-3.77-3.77l6.64-6.639a1 1 0 1 1 1.415 1.414l-6.64 6.64a.67.67 0 0 0 .94.94l7.199-7.198a2.25 2.25 0 0 0-.149-3.16 2.25 2.25 0 0 0-3.154-.142l-7.19 7.191a3.86 3.86 0 0 0 .211 5.445 3.86 3.86 0 0 0 5.445.212l7.191-7.191a1 1 0 0 1 1.415 1.414l-7.192 7.191a5.86 5.86 0 0 1-8.273-.212z"
              fill="#548FF7"
              fillRule="nonzero"
            />
          </svg>
          <span className="text-base leading-normal">
            {isDragging ? 'Drop video here' : 'Attach Video or Drag & Drop'}
          </span>
          <input
            type="file"
            accept="video/mp4,video/quicktime,video/x-msvideo,video/webm"
            onChange={(e) => handleFileChange(e, 'introVideo', 'video', false)}
            className="hidden"
            disabled={uploading || compressing}
          />
        </label>
      </div>
    );
  };

  const FileUploadBox = ({
    fieldName,
    label,
    description,
    accept,
    fileType,
    preview,
    allowMultiple = false,
  }) => {
    const isMultiple = Array.isArray(preview);
    const hasPreviews = isMultiple ? preview && preview.length > 0 : preview;
    const [isDragging, setIsDragging] = React.useState(false);
    const dragCounter = React.useRef(0);

    const handleDragEnter = (e) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounter.current++;
      if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
        setIsDragging(true);
      }
    };

    const handleDragLeave = (e) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounter.current--;
      if (dragCounter.current === 0) {
        setIsDragging(false);
      }
    };

    const handleDragOver = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const handleDrop = async (e) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounter.current = 0;
      setIsDragging(false);

      const files = e.dataTransfer.files;
      if (files && files.length > 0) {
        // Create a synthetic event object
        const syntheticEvent = {
          target: {
            files: allowMultiple ? files : [files[0]],
          },
        };
        await handleFileChange(
          syntheticEvent,
          fieldName,
          fileType,
          allowMultiple
        );
      }
    };

    return (
      <div className="bg-gray-50 rounded-lg p-4">
        <div className="flex flex-col">
          <p className="text-portalother-800 text-sm sm:text-base mb-2 font-semibold">
            {label}
          </p>
          <p className="text-gray-500 text-xs mb-3">{description}</p>

          {hasPreviews ? (
            <div className="space-y-3">
              {isMultiple ? (
                <div className="flex flex-wrap gap-3">
                  {preview.map((prev, index) => (
                    <div key={index} className="relative h-32 w-32">
                      {fileType === 'image' ? (
                        <img
                          src={prev}
                          alt={`${label} ${index + 1}`}
                          className="w-full h-full object-cover rounded-lg border-2 border-green-500"
                        />
                      ) : (
                        <video
                          src={prev}
                          className="w-full h-full object-cover rounded-lg border-2 border-green-500"
                          controls
                        />
                      )}
                      <div className="absolute top-2 left-2 bg-green-500 text-white p-1.5 rounded-full">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4"
                          viewBox="0 0 20 20"
                          fill="currentColor">
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeFile(fieldName, index)}
                        className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded-full hover:bg-red-600 transition-colors">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5"
                          viewBox="0 0 20 20"
                          fill="currentColor">
                          <path
                            fillRule="evenodd"
                            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="relative h-32 w-32">
                  {fileType === 'image' ? (
                    <img
                      src={preview}
                      alt={label}
                      className="w-full h-full object-cover rounded-lg border-2 border-green-500"
                    />
                  ) : (
                    <video
                      src={preview}
                      className="w-full h-full object-cover rounded-lg border-2 border-green-500"
                      controls
                    />
                  )}
                  <div className="absolute top-2 left-2 bg-green-500 text-white p-1.5 rounded-full">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4"
                      viewBox="0 0 20 20"
                      fill="currentColor">
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeFile(fieldName)}
                    className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded-full hover:bg-red-600 transition-colors">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      viewBox="0 0 20 20"
                      fill="currentColor">
                      <path
                        fillRule="evenodd"
                        d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                </div>
              )}

              {/* Allow adding more files if multiple uploads allowed */}
              {allowMultiple && (
                <div
                  className={`flex bg-white border-2 border-dashed rounded-md w-full py-4 px-3 transition-colors ${
                    isDragging
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-portal-300'
                  }`}
                  onDragEnter={handleDragEnter}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}>
                  <label className="relative inline-flex items-center text-md text-portal-300 cursor-pointer w-full justify-center">
                    <svg
                      className="mr-2"
                      width="22"
                      height="22"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M4.93 19.071a5.86 5.86 0 0 1-.213-8.273l7.191-7.191a4.24 4.24 0 0 1 5.982.141 4.24 4.24 0 0 1 .149 5.99l-7.198 7.198a2.67 2.67 0 0 1-3.77-3.77l6.64-6.639a1 1 0 1 1 1.415 1.414l-6.64 6.64a.67.67 0 0 0 .94.94l7.199-7.198a2.25 2.25 0 0 0-.149-3.16 2.25 2.25 0 0 0-3.154-.142l-7.19 7.191a3.86 3.86 0 0 0 .211 5.445 3.86 3.86 0 0 0 5.445.212l7.191-7.191a1 1 0 0 1 1.415 1.414l-7.192 7.191a5.86 5.86 0 0 1-8.273-.212z"
                        fill="#548FF7"
                        fillRule="nonzero"
                      />
                    </svg>
                    <span className="text-base leading-normal">
                      {isDragging
                        ? 'Drop files here'
                        : 'Add More Files or Drag & Drop'}
                    </span>
                    <input
                      type="file"
                      accept={accept}
                      multiple={allowMultiple}
                      onChange={(e) =>
                        handleFileChange(e, fieldName, fileType, allowMultiple)
                      }
                      className="hidden"
                      disabled={uploading || compressing}
                    />
                  </label>
                </div>
              )}
            </div>
          ) : (
            <div
              className={`flex bg-white border-2 border-dashed rounded-md w-full py-4 px-3 transition-colors ${
                isDragging ? 'border-blue-500 bg-blue-50' : 'border-portal-300'
              }`}
              onDragEnter={handleDragEnter}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}>
              <label className="relative inline-flex items-center text-md text-portal-300 cursor-pointer w-full justify-center">
                <svg
                  className="mr-2"
                  width="22"
                  height="22"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M4.93 19.071a5.86 5.86 0 0 1-.213-8.273l7.191-7.191a4.24 4.24 0 0 1 5.982.141 4.24 4.24 0 0 1 .149 5.99l-7.198 7.198a2.67 2.67 0 0 1-3.77-3.77l6.64-6.639a1 1 0 1 1 1.415 1.414l-6.64 6.64a.67.67 0 0 0 .94.94l7.199-7.198a2.25 2.25 0 0 0-.149-3.16 2.25 2.25 0 0 0-3.154-.142l-7.19 7.191a3.86 3.86 0 0 0 .211 5.445 3.86 3.86 0 0 0 5.445.212l7.191-7.191a1 1 0 0 1 1.415 1.414l-7.192 7.191a5.86 5.86 0 0 1-8.273-.212z"
                    fill="#548FF7"
                    fillRule="nonzero"
                  />
                </svg>
                <span className="text-base leading-normal">
                  {isDragging
                    ? 'Drop file here'
                    : allowMultiple
                    ? 'Attach Files or Drag & Drop'
                    : 'Attach File or Drag & Drop'}
                </span>
                <input
                  type="file"
                  accept={accept}
                  multiple={allowMultiple}
                  onChange={(e) =>
                    handleFileChange(e, fieldName, fileType, allowMultiple)
                  }
                  className="hidden"
                  disabled={uploading || compressing}
                />
              </label>
            </div>
          )}

          {errors[fieldName] && (
            <p className="text-red-500 text-sm mt-2">{errors[fieldName]}</p>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="">
      {uploadSuccess ? (
        // Success UI
        <div className="flex items-center justify-center min-h-[600px]">
          <div className="bg-white p-8 max-w-md text-center">
            <div className="mb-6">
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100">
                <svg
                  className="h-10 w-10 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M5 13l4 4L19 7"></path>
                </svg>
              </div>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">
              Upload Successful!
            </h3>
            <p className="text-gray-600 mb-6">
              Your media files have been uploaded successfully. Your application
              status has been updated to "Interview Review".
            </p>
            <div className="space-y-3">
              <button
                onClick={() => navigate('/')}
                className="w-full px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium">
                Go to Dashboard
              </button>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-12 gap-6 border-b pb-2 mb-8 border-slate-300">
            <div className="flex flex-col col-span-full xl:col-span-4 bg-white rounded-sm">
              <div className="flex flex-row">
                <h4 className="text-portal-600 text-2xl font-semibold mr-5 mt-2">
                  Interview Media Upload
                </h4>
              </div>
            </div>
            <div className="flex flex-col col-span-full xl:col-span-8 bg-white  "></div>
          </div>

          {/* {compressing && (
            <div className="mb-6 bg-gradient-to-r from-blue-50 to-blue-100 border-2 border-blue-400 rounded-lg p-6 shadow-lg">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-3 border-blue-600 mr-4"></div>
                  <div>
                    <p className="text-blue-900 font-bold text-lg">
                      Compressing video...
                    </p>
                    <p className="text-blue-700 text-sm">
                      Large videos may take several minutes
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-3xl font-bold text-blue-900">
                    {compressionProgress}%
                  </p>
                </div>
              </div>
              <div className="w-full bg-blue-300 rounded-full h-3 mb-2 shadow-inner">
                <div
                  className="bg-gradient-to-r from-blue-500 to-blue-600 h-3 rounded-full transition-all duration-300 shadow-md"
                  style={{ width: `${compressionProgress}%` }}
                />
              </div>
              <div className="flex items-center justify-between">
                <p className="text-sm text-blue-800 font-medium">
                  ⚠️ Please don't close this page or navigate away
                </p>
                <p className="text-sm text-blue-700">
                  {compressionProgress < 30
                    ? 'Starting compression...'
                    : compressionProgress < 70
                    ? 'Processing video...'
                    : compressionProgress < 100
                    ? 'Almost done...'
                    : 'Complete!'}
                </p>
              </div>
            </div>
          )} */}

          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <FileUploadBox
                fieldName="driversLicense"
                label="Your Driver's License*"
                description="To verify eligibility to complete pickups and deliveries (front and back if needed)"
                accept="image/jpeg,image/png"
                fileType="image"
                preview={previews.driversLicense}
                allowMultiple={true}
              />

              <FileUploadBox
                fieldName="workspaceImages"
                label="Your workspace including machines & folding area*"
                description="To verify you have a dedicated area within your residence to complete orders - please include machines and folding space - can attach multiple images if needed"
                accept="image/jpeg,image/png"
                fileType="image"
                preview={previews.workspaceImages}
                allowMultiple={true}
              />

              <FileUploadBox
                fieldName="washingMachineLabel"
                label="Your washing machine model number label*"
                description="(This can be found inside the lid or the back of your machine) - to verify machine capacity - can attach multiple images if needed"
                accept="image/jpeg,image/png"
                fileType="image"
                preview={previews.washingMachineLabel}
                allowMultiple={true}
              />

              <FileUploadBox
                fieldName="foldedItemsImages"
                label="Variety of items you have folded*"
                description="To verify quality is on par with our client quality expectations - please submit images of shirts, pants, towels, and sheets - can attach multiple images if needed"
                accept="image/jpeg,image/png"
                fileType="image"
                preview={previews.foldedItemsImages}
                allowMultiple={true}
              />

              <div className="md:col-span-2">
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-portalother-800 text-sm sm:text-base mb-4 font-semibold">
                    Self-Recorded Video Interview
                  </p>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Left Column - Instructions and Questions */}
                    <div className="flex flex-col">
                      <div className="bg-white rounded-lg p-4 mb-3 border border-gray-200">
                        <div className="flex items-center mb-3">
                          <svg
                            className="h-5 w-5 text-gray-600 mr-2"
                            fill="currentColor"
                            viewBox="0 0 20 20">
                            <path
                              fillRule="evenodd"
                              d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z"
                              clipRule="evenodd"
                            />
                          </svg>
                          <p className="text-gray-700 font-medium">
                            Self-Recorded Interview Instructions
                          </p>
                        </div>
                        <div className="text-gray-600 text-sm space-y-2 mb-4">
                          <p>Make sure your face is clearly visible.</p>
                          <p>Record in a quiet, well-lit space.</p>
                          <p>Make sure we can hear you clearly.</p>
                        </div>
                        <div className="border-t pt-4 mt-4">
                          <p className="font-medium text-gray-700 mb-3">
                            Questions:
                          </p>
                          <ol className="list-decimal list-inside space-y-2 text-sm text-gray-600">
                            <li>
                              Tell us a little about yourself and what interests
                              you most about partnering with Laundry Care.
                            </li>
                            <li>
                              What type of experience do you have that relates
                              to this role (laundry, customer service, or
                              independent work)?
                            </li>
                            <li>
                              Imagine you arrive for a scheduled pickup, but the
                              laundry isn't left out. How would you handle that
                              situation?
                            </li>
                            <li>
                              This role requires managing pickups, deliveries,
                              and communication on your own. What steps or tools
                              would you use to stay organized?
                            </li>
                            <li>
                              It takes consistency and reliability to build
                              returning clients. How would you ensure clients
                              have a positive, dependable experience with you
                              each time?
                            </li>
                          </ol>
                        </div>
                      </div>
                    </div>

                    {/* Right Column - Video Upload */}
                    <div className="flex flex-col justify-center">
                      {compressing ? (
                        <div className="bg-blue-50 border-2 flex justify-center items-center h-full mb-3 border-blue-300 rounded-lg p-6">
                          <div className="flex flex-col items-center justify-center ">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-blue-600 mb-4"></div>
                            <p className="text-blue-800 font-semibold text-lg mb-2">
                              Compressing video...
                            </p>
                            <p className="text-blue-600 text-sm mb-4">
                              This may take a few minutes for large videos.
                              Please don't close this page.
                            </p>
                            <div className="w-full bg-blue-200 rounded-full h-4 mb-2">
                              <div
                                className="bg-blue-600 h-4 rounded-full transition-all duration-300 flex items-center justify-center"
                                style={{ width: `${compressionProgress}%` }}>
                                <span className="text-white text-xs font-bold">
                                  {compressionProgress}%
                                </span>
                              </div>
                            </div>
                            <p className="text-blue-700 text-sm font-medium">
                              {compressionProgress}% complete
                            </p>
                          </div>
                        </div>
                      ) : previews.introVideo ? (
                        <div className="relative">
                          <video
                            src={previews.introVideo}
                            className="w-full object-cover rounded-lg border-2 border-green-500"
                            controls
                          />
                          <div className="absolute top-2 left-2 bg-green-500 text-white px-2 py-1 rounded-full text-xs flex items-center">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-4 w-4"
                              viewBox="0 0 20 20"
                              fill="currentColor">
                              <path
                                fillRule="evenodd"
                                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeFile('introVideo')}
                            className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded-full hover:bg-red-600 transition-colors">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-5 w-5"
                              viewBox="0 0 20 20"
                              fill="currentColor">
                              <path
                                fillRule="evenodd"
                                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </button>
                        </div>
                      ) : (
                        <VideoDropZone />
                      )}

                      {errors.introVideo && (
                        <p className="text-red-500 text-sm mt-2">
                          {errors.introVideo}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {uploading && (
              <div className="mb-6">
                <div className="w-full bg-gray-200 rounded-full h-4">
                  <div
                    className="bg-blue-500 h-4 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
                <p className="text-sm text-gray-600 mt-2 text-center">
                  Uploading... {Math.round(uploadProgress)}%
                </p>
              </div>
            )}

            {compressing && (
              <div className="mb-4 bg-yellow-50 border border-yellow-300 rounded-lg p-4">
                <p className="text-yellow-800 text-sm font-medium">
                  ⏳ Video compression in progress. Please wait before
                  submitting...
                </p>
              </div>
            )}

            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={() => {
                  navigate('/');
                  window.scrollTo(0, 0);
                }}
                disabled={uploading || compressing}
                className="px-6 py-3 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                Cancel
              </button>
              <button
                type="submit"
                disabled={uploading || compressing}
                className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                {compressing
                  ? 'Compressing Video...'
                  : uploading
                  ? 'Uploading...'
                  : 'Submit Media'}
              </button>
            </div>
          </form>
        </>
      )}
    </div>
  );
};

export default InterviewMediaUpload;
